<script setup lang="ts">
import { computed, type PropType } from 'vue'
import {
    PriceComponentType,
    type ChargePriceSimulation
} from '../../../../interfaces/simulation'
import ResultsSummary from './ResultsSummary.vue'

const props = defineProps({
    priceDetails: {
        type: Object as PropType<ChargePriceSimulation>,
        default: undefined
    },
    s3Url: {
        type: String,
        required: true
    },
    isBoost: {
        type: Boolean,
        default: false
    },
    isPriceComputable: {
        type: Boolean,
        default: true
    },
    isVehicleCompatible: {
        type: Boolean,
        default: true
    },
    isMissingVehicle: {
        type: Boolean,
        default: false
    },
    isUserConnected: {
        type: Boolean,
        default: false
    },
    isDisabled: {
        type: Boolean,
        default: false
    },
    isError: {
        type: Boolean,
        default: false
    }
})

defineEmits([
    'seeDetails',
    'retry',
    'authenticateUser',
    'addVehicle',
    'modifyVehicle',
    'redirectToBoostLanding'
])

const showResultsSummary = computed(
    () =>
        (props.priceDetails &&
            props.isUserConnected &&
            !props.isMissingVehicle &&
            props.isVehicleCompatible &&
            props.isPriceComputable) ||
        props.isError ||
        props.isDisabled
)

const amountToSave = computed(() => {
    return (
        parseFloat(
            props.priceDetails?.taxedPriceComponents.find(
                (c) => c.type === PriceComponentType.MARGIN
            )?.price.amount ?? '0'
        ) / 100
    )
})
</script>

<template>
    <div class="w-full flex flex-col items-center justify-center">
        <span
            class="w-full text-xs 425:text-sm font-medium tracking-wider text-header-dark text-center mt-1 mb-4 uppercase"
        >
            {{ $t('simulation.charge.results.title') }}
        </span>
        <div
            class="w-full flex flex-col border border-secondary-gray-disabled bg-white rounded-2xl gap-2 425:gap-4 items-center justify-center p-4 425:p-6"
        >
            <div
                v-if="showResultsSummary"
                class="w-full flex flex-col gap-4 items-center justify-center"
                data-testid="results-summary"
            >
                <ResultsSummary
                    :price-details
                    :is-boost
                    :id-disabled="isDisabled || isError"
                />
                <span
                    v-if="isError"
                    class="text-badge-negative text-[13px] 425:text-[15px] font-semibold leading-4 425:leading-[18px] text-center"
                >
                    {{ $t('simulation.charge.results.error') }}
                </span>
                <span
                    v-else-if="$slots.altText"
                    class="w-full px-2 425:p-0 text-gray-l-500 text-[13px] 425:text-[15px] font-normal leading-4 425:leading-6 text-start"
                >
                    <slot name="altText" />
                </span>
                <ChargemapB2CButton
                    type="secondary"
                    size="large"
                    class="w-full 425:w-fit"
                    :disabled="isDisabled"
                    @click="isError ? $emit('retry') : $emit('seeDetails')"
                >
                    {{
                        $t(
                            `simulation.charge.results.actions.${isError ? 'retry' : 'details'}`
                        )
                    }}
                </ChargemapB2CButton>

                <BoostCtaBanner
                    v-if="!isBoost && !isError && !isDisabled"
                    :amount-to-save
                    @redirect="$emit('redirectToBoostLanding')"
                />
            </div>

            <div
                v-else-if="!isUserConnected || isMissingVehicle"
                data-testid="missing-vehicle"
                class="flex flex-col gap-2 425:gap-4 items-center justify-center"
            >
                <span
                    class="text-gray-l-800 text-[15px] 425:text-[17px] font-semibold leading-[18px] 425:leading-5 text-center"
                >
                    {{ $t('simulation.charge.results.estimate.title') }}
                </span>
                <img
                    class="w-[260px] 425:w-[320px]"
                    :src="`${s3Url}/simulator/ChargeEstimatorMobile.svg`"
                    :alt="$t('simulation.charge.results.estimate.image_alt')"
                />
                <ChargemapB2CButton
                    v-if="isUserConnected"
                    type="secondary"
                    size="large"
                    class="w-full 425:w-fit"
                    @click="$emit('addVehicle')"
                >
                    {{ $t('simulation.charge.results.actions.add_vehicle') }}
                </ChargemapB2CButton>
                <ChargemapB2CButton
                    v-else
                    type="secondary"
                    size="large"
                    class="w-full 425:w-fit"
                    @click="$emit('authenticateUser')"
                >
                    {{ $t('simulation.charge.results.actions.estimate') }}
                </ChargemapB2CButton>
            </div>
            <div
                v-else
                data-testid="incompatible-vehicle"
                class="w-full flex flex-col gap-2 425:gap-4 items-center justify-center text-center"
            >
                <span
                    class="text-gray-l-500 text-[13px] 425:text-[17px] font-normal leading-5 425:leading-[27px]"
                >
                    {{
                        !isPriceComputable
                            ? $t(
                                  'simulation.charge.results.incompatible.station'
                              )
                            : $t(
                                  'simulation.charge.results.incompatible.vehicle.label'
                              )
                    }}
                </span>
                <a
                    v-if="!isVehicleCompatible && isPriceComputable"
                    class="text-light-blue-700 text-[13px] 425:text-[15px] font-semibold leading-4 425:leading-5 cursor-pointer"
                    @click="$emit('modifyVehicle')"
                >
                    {{
                        $t(
                            'simulation.charge.results.incompatible.vehicle.action'
                        )
                    }}
                </a>
            </div>
        </div>
    </div>
</template>
